import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAcceptCpmTermsMutation } from "@/generated/graphql";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: string;
  onAccepted?: () => void | Promise<unknown>;
};

const pdfUrlForVersion = (version: string) => `/legal/cpm-terms-v${version}.pdf`;

export const CpmTermsModal = ({ open, onOpenChange, version, onAccepted }: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [agreed, setAgreed] = useState(false);
  const [acceptCpmTerms, { loading }] = useAcceptCpmTermsMutation();

  const pdfUrl = pdfUrlForVersion(version);

  const handleAccept = async () => {
    try {
      await acceptCpmTerms({ variables: { version } });
      // Wait for the parent to refetch (myData uses no-cache, so the gate
      // depends on the refetch completing) before closing the modal —
      // prevents a flash of the overlay still being up.
      await onAccepted?.();
      toast({ title: t("campaign.terms.accepted") });
      setAgreed(false);
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to accept CPM terms:", err);
      toast({
        title: t("campaign.terms.errorGeneric"),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setAgreed(false);
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>{t("campaign.terms.title")}</DialogTitle>
              <DialogDescription>
                {t("campaign.terms.versionLabel", { version })}
              </DialogDescription>
            </div>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline shrink-0"
            >
              <ExternalLink className="w-4 h-4" />
              {t("campaign.terms.download")}
            </a>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden rounded-md border bg-muted">
          <iframe
            src={pdfUrl}
            title={t("campaign.terms.title")}
            className="w-full h-full min-h-[60vh]"
          />
        </div>

        <div className="flex items-start gap-2 pt-2">
          <Checkbox
            id="cpm-terms-agree"
            checked={agreed}
            onCheckedChange={(next) => setAgreed(next === true)}
          />
          <Label htmlFor="cpm-terms-agree" className="text-sm leading-snug cursor-pointer">
            {t("campaign.terms.acceptCheckbox")}
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t("campaign.terms.cancel")}
          </Button>
          <Button onClick={handleAccept} disabled={!agreed || loading}>
            {loading ? t("campaign.terms.accepting") : t("campaign.terms.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CpmTermsModal;
